import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { AlertTriangle, CalendarDays, Plus } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, type DepartmentRef, type ProjectRef, type UserRef } from '../lib/api';
import { P, useAuth } from '../lib/auth';
import { enumLabel, useI18n } from '../lib/i18n';
import { formatDate } from '../lib/format';
import { anyFilter, useKeptFilters } from '../lib/kept-filters';
import { urgentFirst } from '../lib/task-order';
import { WorkflowStep } from '../components/WorkflowChain';
import { ChecklistProgressChip } from '../components/TaskChecklist';
import { TaskHolder } from '../components/TaskHolder';
import {
  Badge,
  Button,
  Card,
  ErrorState,
  Field,
  Input,
  LoadingBlock,
  PageHeader,
  Select,
  priorityTone,
  useToast,
} from '../components/ui';

/** §8.5 — the five visible columns. CANCELLED is deliberately absent. */
const COLUMNS = ['TO_DO', 'IN_PROGRESS', 'BLOCKED', 'REVIEW', 'DONE'] as const;

/**
 * Mirrors §43.1 so an impossible drop is refused before the request goes out.
 * Each card also says where this person may drop it (`moves`, 2026-10-11): only
 * whoever created a task or gave it out marks it done, and a workflow's step
 * with a level after it is finished straight to Done — as is a task by its own
 * creator, who needs no review (2026-10-28).
 */
const ALLOWED: Record<string, string[]> = {
  TO_DO: ['IN_PROGRESS', 'CANCELLED'],
  IN_PROGRESS: ['BLOCKED', 'REVIEW', 'TO_DO', 'CANCELLED'],
  BLOCKED: ['IN_PROGRESS', 'CANCELLED'],
  REVIEW: ['IN_PROGRESS', 'DONE'],
  DONE: ['REVIEW'],
  CANCELLED: [],
};

export function KanbanPage() {
  const { t, locale, localized } = useI18n();
  const { can } = useAuth();
  // As the API's taskScope: without these, someone sees only the tasks given to them or made by them.
  const seesOthersTasks = can(P.TASKS_ASSIGN) || can(P.TASKS_MANAGE_ALL) || can(P.PROJECTS_VIEW_ALL);
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();

  // Kept until cleared, as on the lists: a task opened from its title and closed finds the board as it was left.
  const [filters, setFilters] = useKeptFilters('kanban');
  const [dragging, setDragging] = useState<any>(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['kanban', filters],
    queryFn: () => api.get<any>('/tasks/kanban', filters),
  });

  const { data: projects } = useQuery({
    queryKey: ['projects', 'options'],
    queryFn: () => api.get<ProjectRef[]>('/projects/options'),
  });

  const move = useMutation({
    mutationFn: ({ taskId, status }: { taskId: string; status: string }) =>
      api.post(`/tasks/${taskId}/status`, { status }),
    // Optimistic UI with rollback — §8.5 asks for exactly this.
    onMutate: async ({ taskId, status }) => {
      await queryClient.cancelQueries({ queryKey: ['kanban', filters] });
      const previous = queryClient.getQueryData<any>(['kanban', filters]);

      queryClient.setQueryData<any>(['kanban', filters], (current: any) => {
        if (!current) return current;
        let card: any;
        const columns = current.columns.map((col: any) => {
          const found = col.cards.find((c: any) => c.id === taskId);
          if (found) card = found;
          return { ...col, cards: col.cards.filter((c: any) => c.id !== taskId) };
        });
        if (!card) return current;
        return {
          ...current,
          // At the top of its column — under the urgent cards, unless it is one (2026-10-11).
          columns: columns.map((col: any) =>
            col.status === status ? { ...col, cards: urgentFirst([{ ...card, status }, ...col.cards]) } : col,
          ),
        };
      });

      return { previous };
    },
    onError: (err: any, _vars, context) => {
      queryClient.setQueryData(['kanban', filters], context?.previous);
      toast.error(err.message ?? t('kanban.invalidMove'));
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['kanban'] });
      void queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });

  const onDragStart = (event: DragStartEvent) => setDragging(event.active.data.current?.card ?? null);

  const onDragEnd = (event: DragEndEvent) => {
    setDragging(null);
    const card = event.active.data.current?.card;
    const target = event.over?.id as string | undefined;
    if (!card || !target || card.status === target) return;

    const allowed: string[] = card.moves ?? ALLOWED[card.status] ?? [];
    if (!allowed.includes(target)) {
      // Done is the review's answer: whoever has the task moves it to Review instead.
      toast.error(t(target === 'DONE' && card.status !== 'DONE' ? 'kanban.doneByReviewer' : 'kanban.invalidMove'));
      return;
    }
    move.mutate({ taskId: card.id, status: target });
  };

  if (isLoading) return <LoadingBlock />;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;

  return (
    <>
      <PageHeader
        title={t('kanban.title')}
        subtitle={seesOthersTasks ? t('kanban.dropHint') : `${t('tasks.ownOnlyHint')} ${t('kanban.dropHint')}`}
        action={
          can(P.TASKS_CREATE) && (
            <Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => navigate('/tasks/new')}>
              {t('tasks.new')}
            </Button>
          )
        }
      />

      <Card className="mb-4" padded={false}>
        <div className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-5">
          <Select
            value={filters.projectId ?? ''}
            onChange={(e) => setFilters({ ...filters, projectId: e.target.value, generalOnly: '' })}
          >
            <option value="">{t('common.project')} — {t('common.all')}</option>
            {projects?.map((p) => (
              <option key={p.id} value={p.id}>{localized(p)}</option>
            ))}
          </Select>
          <Select value={filters.priority ?? ''} onChange={(e) => setFilters({ ...filters, priority: e.target.value })}>
            <option value="">{t('common.priority')} — {t('common.all')}</option>
            {['LOW', 'MEDIUM', 'HIGH', 'URGENT'].map((p) => (
              <option key={p} value={p}>{enumLabel(p, locale)}</option>
            ))}
          </Select>
          {/* To me, or by me: choosing one lets go of the other, as on the Tasks list. */}
          <Button
            variant={filters.mine === 'true' ? 'primary' : 'secondary'}
            onClick={() => setFilters({ ...filters, mine: filters.mine === 'true' ? '' : 'true', assignedByMe: '' })}
          >
            {t('kanban.myTasks')}
          </Button>
          <Button
            variant={filters.assignedByMe === 'true' ? 'primary' : 'secondary'}
            onClick={() =>
              setFilters({ ...filters, assignedByMe: filters.assignedByMe === 'true' ? '' : 'true', mine: '' })
            }
          >
            {t('tasks.assignedByMe')}
          </Button>
          <Button
            variant={filters.generalOnly === 'true' ? 'primary' : 'secondary'}
            onClick={() =>
              setFilters({ ...filters, generalOnly: filters.generalOnly === 'true' ? '' : 'true', projectId: '' })
            }
          >
            {t('common.general')}
          </Button>
        </div>
        {anyFilter(filters) && (
          <div className="flex flex-wrap gap-2 border-t hairline px-4 py-2.5">
            <Button size="sm" variant="ghost" onClick={() => setFilters({})}>
              {t('common.clear')}
            </Button>
          </div>
        )}
      </Card>

      <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd}>
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
          {COLUMNS.map((status) => {
            const column = data?.columns.find((c: any) => c.status === status);
            return <Column key={status} status={status} cards={column?.cards ?? []} />;
          })}
        </div>

        <DragOverlay>{dragging && <TaskCard card={dragging} overlay />}</DragOverlay>
      </DndContext>

    </>
  );
}

function Column({ status, cards }: { status: string; cards: any[] }) {
  const { t, locale } = useI18n();
  const { setNodeRef, isOver } = useDroppable({ id: status });

  return (
    <div
      ref={setNodeRef}
      className={clsx(
        'flex min-h-[200px] flex-col rounded-lg border transition-colors duration-fast',
        isOver ? 'border-primary bg-primary/6' : 'hairline bg-[var(--surface-sunken)]',
      )}
    >
      <div className="flex items-center justify-between gap-2 border-b hairline px-3 py-2.5">
        <span className="text-body-sm font-semibold text-[var(--text-primary)]">
          {enumLabel(status, locale)}
        </span>
        <span className="rounded-pill bg-[var(--surface-card)] px-2 py-0.5 text-caption tabular text-muted">
          {cards.length}
        </span>
      </div>
      <div className="flex-1 space-y-2 p-2">
        {cards.map((card) => (
          <DraggableCard key={card.id} card={card} />
        ))}
        {cards.length === 0 && (
          <p className="px-2 py-6 text-center text-caption text-faint">{t('common.none')}</p>
        )}
      </div>
    </div>
  );
}

function DraggableCard({ card }: { card: any }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: card.id,
    data: { card },
  });

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={clsx('cursor-grab active:cursor-grabbing', isDragging && 'opacity-40')}
    >
      <TaskCard card={card} />
    </div>
  );
}

function TaskCard({ card, overlay = false }: { card: any; overlay?: boolean }) {
  const { t, locale, localized } = useI18n();

  return (
    <div
      className={clsx(
        'rounded-md border p-3 transition-shadow',
        overlay ? 'border-primary bg-[var(--surface-card)] shadow-lg rotate-2' : 'hairline bg-[var(--surface-card)]',
      )}
    >
      {/* The title opens the task, or a new tab (operator, 2026-10-22). A click is not a drag: the card
          moves only after 6px. The browser's own dragging of a link is off, so it never fights the card's. */}
      <p className="mb-2 text-body-sm font-medium text-[var(--text-primary)]">
        {overlay ? (
          card.title
        ) : (
          <Link to={`/tasks/${card.id}`} draggable={false} className="hover:underline">
            {card.title}
          </Link>
        )}
      </p>
      <WorkflowStep task={card} className="mb-2 max-w-full" />

      <div className="mb-2 flex flex-wrap items-center gap-1.5">
        <Badge tone={priorityTone(card.priority)}>{enumLabel(card.priority, locale)}</Badge>
        <Badge tone={card.isGeneral ? 'neutral' : 'plasma'}>
          {card.isGeneral ? t('common.general') : localized(card.project)}
        </Badge>
        {card.isOverdue && (
          <Badge tone="error" dot>
            {t('tickets.overdue')}
          </Badge>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3 text-caption text-muted">
        {/* A person, or everyone in the department that shares it (2026-10-10). */}
        <TaskHolder task={card} icon className="max-w-full" />
        {card.dueAt && (
          <span className={clsx('inline-flex items-center gap-1 tabular', card.isOverdue && 'text-error-fg dark:text-error-dark')}>
            <CalendarDays className="h-3.5 w-3.5" aria-hidden />
            {formatDate(card.dueAt, locale)}
          </span>
        )}
        <ChecklistProgressChip progress={card.checklistProgress} />
      </div>
    </div>
  );
}
